/**
 * Bulk Upload Modal for Guest Blog Sites
 * Multi-step process: File Upload -> Column Mapping -> Preview -> Save
 */

import React, { useState, useCallback } from 'react';
import {
  CloudUpload as UploadIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';

import { bulkUploadService, type ColumnMapping, type PreviewRow } from '../../services/bulkUploadService';
import { clientService } from '../../services/clientService';
import FileUploadStep from './FileUploadStep';
import ColumnMappingStep from './ColumnMappingStep';
import PreviewTableStep from './PreviewTableStep';

interface BulkUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (message?: string) => void;
}

const steps = ['Upload File', 'Map Columns', 'Preview & Validate', 'Save'];

const BulkUploadModal: React.FC<BulkUploadModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [file, setFile] = useState<File | null>(null);
  const [parseData, setParseData] = useState<any>(null);
  const [columnMappings, setColumnMappings] = useState<ColumnMapping[]>([]);
  const [previewData, setPreviewData] = useState<PreviewRow[]>([]);
  const [selectedClient, setSelectedClient] = useState<string>('');
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  // Live progress for chunked saves (>100 rows). Falsy when not chunking.
  const [saveProgress, setSaveProgress] = useState<{
    processed: number;
    total: number;
    percent: number;
    batchIndex: number;
    totalBatches: number;
  } | null>(null);

  // Get clients for dropdown
  const { data: clientsData } = useQuery({
    queryKey: ['clients'],
    queryFn: () => clientService.getClients({ limit: 1000 }),
  });

  const clients = clientsData?.data || [];

  const handleReset = useCallback(() => {
    setActiveStep(0);
    setFile(null);
    setParseData(null);
    setColumnMappings([]);
    setPreviewData([]);
    setSelectedClient('');
    setSelectedRows([]);
    setError('');
    setSaveProgress(null);
  }, []);

  const handleClose = useCallback(() => {
    handleReset();
    onClose();
  }, [handleReset, onClose]);


  const handleFileUpload = async (uploadedFile: File, clientId?: string) => {
    try {
      setLoading(true);
      setError('');
      setFile(uploadedFile);
      setSelectedClient(clientId || '');

      const data = await bulkUploadService.parseFile(uploadedFile, clientId);
      setParseData(data);
      setColumnMappings(data.autoMappings);
      setActiveStep(1);
    } catch (error: any) {
      setError('Failed to parse file: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleMappingComplete = async (mappings: ColumnMapping[]) => {
    try {
      setLoading(true);
      setError('');
      setColumnMappings(mappings);

      if (!file) {
        throw new Error('No file selected');
      }

      const preview = await bulkUploadService.generatePreview(
        file,
        mappings,
        selectedClient || undefined
      );

      setPreviewData(preview.previewData);
      // Select all valid rows by default
      const validRowIndexes = preview.previewData
        .filter(row => row.isValid)
        .map(row => row.rowIndex);
      setSelectedRows(validRowIndexes);
      setActiveStep(2);
    } catch (error: any) {
      setError('Failed to generate preview: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePreviewComplete = (selectedRowIndexes: number[]) => {
    setSelectedRows(selectedRowIndexes);
    setActiveStep(3);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError('');
      setSaveProgress(null);

      const result = await bulkUploadService.savePreviewData(previewData, selectedRows, {
        chunkSize: 100,
        onProgress: (info) => setSaveProgress(info),
      });

      // Use the detailed message from backend
      const backendMessage = result.summary?.message || result.message || 'Bulk upload completed successfully!';

      if (result.errors.length > 0) {
        setError(`${backendMessage}. Errors: ${result.errors.join(', ')}`);
      } else {
        onSuccess(backendMessage);
        handleClose();
      }
    } catch (error: any) {
      setError('Failed to save data: ' + error.message);
    } finally {
      setLoading(false);
      setSaveProgress(null);
    }
  };

  const handleBack = () => {
    setActiveStep(prev => Math.max(0, prev - 1));
    setError('');
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <FileUploadStep
            onFileUpload={handleFileUpload}
            clients={clients}
            selectedClient={selectedClient}
            onClientChange={setSelectedClient}
            loading={loading}
          />
        );
      case 1:
        return (
          <ColumnMappingStep
            availableColumns={parseData?.availableColumns || []}
            guestBlogSiteColumns={parseData?.guestBlogSiteColumns || []}
            initialMappings={columnMappings}
            onMappingComplete={handleMappingComplete}
            loading={loading}
          />
        );
      case 2:
        return (
          <PreviewTableStep
            previewData={previewData}
            selectedRows={selectedRows}
            onSelectionChange={setSelectedRows}
            onComplete={handlePreviewComplete}
            loading={loading}
          />
        );
      case 3:
        return (
          <div className="bulk-ready-state">
            <h6>Ready to Save</h6>
            <p>{selectedRows.length} rows selected for import</p>
            <div className="bulk-ready-alert">
              Click "Save" to import the selected guest blog sites to your database.
              This action cannot be undone.
            </div>

            {loading && saveProgress && saveProgress.totalBatches > 1 && (
              <div className="bulk-progress-wrap">
                <div className="bulk-progress-label">
                  Uploading in batches of 100… {saveProgress.processed}/{saveProgress.total} rows ·
                  {' '}{saveProgress.percent}% (batch {saveProgress.batchIndex} of {saveProgress.totalBatches})
                </div>
                <div className="bulk-progress-track">
                  <div
                    className="bulk-progress-bar"
                    style={{ width: `${saveProgress.percent}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  const getActionButtons = () => {
    switch (activeStep) {
      case 0:
        return (
          <>
            <button className="bulk-btn bulk-btn-secondary" onClick={handleClose}>
              Cancel
            </button>
          </>
        );
      case 1:
        return (
          <>
            <button className="bulk-btn bulk-btn-secondary" onClick={handleBack}>
              Back
            </button>
            <button className="bulk-btn bulk-btn-secondary" onClick={handleClose}>
              Cancel
            </button>
          </>
        );
      case 2:
        return (
          <>
            <button className="bulk-btn bulk-btn-secondary" onClick={handleBack}>
              Back
            </button>
            <button className="bulk-btn bulk-btn-secondary" onClick={handleClose}>
              Cancel
            </button>
          </>
        );
      case 3:
        return (
          <>
            <button className="bulk-btn bulk-btn-secondary" onClick={handleBack}>
              Back
            </button>
            <button className="bulk-btn bulk-btn-secondary" onClick={handleClose}>
              Cancel
            </button>
            <button
              className="bulk-btn bulk-btn-primary"
              onClick={handleSave}
              disabled={loading || selectedRows.length === 0}
            >
              {loading ? (
                <>
                  <div className="bulk-spinner" />
                  Saving...
                </>
              ) : (
                <>
                  <UploadIcon />
                  Save {selectedRows.length} Sites
                </>
              )}
            </button>
          </>
        );
      default:
        return (
          <button className="bulk-btn bulk-btn-secondary" onClick={handleClose}>
            Close
          </button>
        );
    }
  };

  return (
    <>
      <style>{`
        .bulk-upload-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.6);
          backdrop-filter: blur(8px);
          z-index: 1300;
          display: ${isOpen ? 'flex' : 'none'};
          align-items: center;
          justify-content: center;
          padding: 20px;
          animation: fadeIn 0.3s ease-out;
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .bulk-upload-modal {
          background: #ffffff;
          border-radius: 12px;
          max-width: 70vw;
          width: 100%;
          max-height: 85vh;
          overflow: hidden;
          box-shadow: 
            0 10px 25px rgba(0, 0, 0, 0.15),
            0 4px 10px rgba(0, 0, 0, 0.1);
          display: flex;
          flex-direction: column;
          min-height: 500px;
          position: relative;
          animation: slideUp 0.3s ease-out;
          border: 1px solid #e2e8f0;
        }
        @keyframes slideUp {
          from { 
            opacity: 0;
            transform: translateY(30px) scale(0.95);
          }
          to { 
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        .bulk-upload-modal::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 1px;
          background: linear-gradient(90deg, 
            transparent, 
            rgba(255, 255, 255, 0.5), 
            transparent);
        }
        .bulk-upload-header {
          background: #f8fafc;
          padding: 20px 24px;
          border-bottom: 1px solid #e2e8f0;
          position: relative;
        }
        .bulk-upload-header::after {
          content: '';
          position: absolute;
          bottom: -1px;
          left: 32px;
          right: 32px;
          height: 1px;
          background: linear-gradient(90deg, 
            transparent, 
            rgba(255, 255, 255, 0.3), 
            transparent);
        }
        .bulk-upload-title {
          color: #1e293b;
          font-size: 18px;
          font-weight: 600;
          margin: 0;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .bulk-upload-title::before {
          content: '📊';
          font-size: 20px;
        }
        .bulk-upload-content {
          background: #ffffff;
          margin: 16px;
          border-radius: 8px;
          padding: 20px;
          flex: 1;
          overflow-y: auto;
          border: 1px solid #e2e8f0;
        }
        .bulk-upload-stepper {
          display: flex;
          justify-content: space-between;
          margin-bottom: 24px;
          position: relative;
          padding: 0 16px;
        }
        .bulk-upload-stepper::before {
          content: '';
          position: absolute;
          top: 20px;
          left: 60px;
          right: 60px;
          height: 2px;
          background: #e2e8f0;
          z-index: 0;
          border-radius: 1px;
        }
        .bulk-step {
          display: flex;
          flex-direction: column;
          align-items: center;
          position: relative;
          z-index: 1;
          background: transparent;
          padding: 0 20px;
          transition: transform 0.3s ease;
        }
        .bulk-step:hover {
          transform: translateY(-2px);
        }
        .bulk-step-circle {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: #f1f5f9;
          color: #64748b;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: 14px;
          margin-bottom: 8px;
          transition: all 0.3s ease;
          border: 2px solid #e2e8f0;
          position: relative;
        }
        .bulk-step-circle::after {
          content: '';
          position: absolute;
          inset: -2px;
          border-radius: 50%;
          padding: 2px;
          background: linear-gradient(135deg, #667eea, #764ba2);
          mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
          mask-composite: exclude;
          opacity: 0;
          transition: opacity 0.3s;
        }
        .bulk-step.active .bulk-step-circle {
          background: #3b82f6;
          color: white;
          border-color: #3b82f6;
          box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
          transform: scale(1.05);
        }
        .bulk-step.active .bulk-step-circle::after {
          opacity: 1;
        }
        .bulk-step.completed .bulk-step-circle {
          background: #10b981;
          color: white;
          border-color: #10b981;
        }
        .bulk-step-label {
          font-size: 12px;
          color: #64748b;
          text-align: center;
          max-width: 100px;
          font-weight: 500;
          transition: all 0.2s ease;
        }
        .bulk-step.active .bulk-step-label {
          color: #3b82f6;
          font-weight: 600;
        }
        .bulk-step.completed .bulk-step-label {
          color: #10b981;
          font-weight: 600;
        }
        .bulk-upload-error {
          background: #fef2f2;
          border: 1px solid #fecaca;
          border-radius: 6px;
          padding: 12px 16px;
          margin-bottom: 16px;
          font-size: 13px;
          color: #991b1b;
          border-left: 3px solid #ef4444;
        }
        .bulk-upload-footer {
          background: #f8fafc;
          padding: 16px 24px;
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          border-top: 1px solid #e2e8f0;
        }
        .bulk-btn {
          padding: 8px 16px;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          border: 1px solid;
          font-family: inherit;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .bulk-btn svg {
          width: 16px;
          height: 16px;
        }
        .bulk-btn-secondary {
          background: #ffffff;
          color: #64748b;
          border-color: #e2e8f0;
        }
        .bulk-btn-secondary:hover {
          background: #f8fafc;
          border-color: #cbd5e1;
        }
        .bulk-btn-primary {
          background: #3b82f6;
          color: white;
          border-color: #3b82f6;
        }
        .bulk-btn-primary:hover {
          background: #2563eb;
          border-color: #2563eb;
        }
        .bulk-btn-primary:disabled {
          background: #94a3b8;
          border-color: #94a3b8;
          cursor: not-allowed;
        }
        .bulk-spinner {
          width: 16px;
          height: 16px;
          border: 2px solid #E2E8F0;
          border-top-color: white;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        .bulk-ready-state {
          text-align: center;
          padding: 32px 20px;
        }
        .bulk-ready-state h6 {
          font-size: 18px;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 8px;
        }
        .bulk-ready-state p {
          color: #64748b;
          margin-bottom: 16px;
          font-size: 14px;
        }
        .bulk-ready-alert {
          background: #eff6ff;
          border: 1px solid #bfdbfe;
          border-radius: 6px;
          padding: 12px 16px;
          margin-top: 16px;
          font-size: 13px;
          color: #1e40af;
          max-width: 400px;
          margin-left: auto;
          margin-right: auto;
          border-left: 3px solid #3b82f6;
        }
        .bulk-progress-wrap {
          margin-top: 20px;
          max-width: 480px;
          margin-left: auto;
          margin-right: auto;
          text-align: left;
        }
        .bulk-progress-label {
          font-size: 13px;
          color: #475569;
          margin-bottom: 8px;
        }
        .bulk-progress-track {
          height: 8px;
          background: #e2e8f0;
          border-radius: 999px;
          overflow: hidden;
        }
        .bulk-progress-bar {
          height: 100%;
          background: linear-gradient(90deg, #3b82f6, #2563eb);
          border-radius: 999px;
          transition: width 0.3s ease;
        }
      `}</style>
      <div className="bulk-upload-modal-overlay" onClick={(e) => e.target === e.currentTarget && handleClose()}>
        <div className="bulk-upload-modal" onClick={(e) => e.stopPropagation()}>
          <div className="bulk-upload-header">
            <h2 className="bulk-upload-title">Bulk Upload Guest Blog Sites</h2>
          </div>

          <div className="bulk-upload-content">
            <div className="bulk-upload-stepper">
              {steps.map((label, index) => (
                <div key={label} className={`bulk-step ${
                  index === activeStep ? 'active' : 
                  index < activeStep ? 'completed' : ''
                }`}>
                  <div className="bulk-step-circle">
                    {index < activeStep ? '✓' : index + 1}
                  </div>
                  <div className="bulk-step-label">{label}</div>
                </div>
              ))}
            </div>

            {error && (
              <div className="bulk-upload-error">
                {error}
              </div>
            )}

        {renderStepContent()}
          </div>

          <div className="bulk-upload-footer">
            {getActionButtons()}
          </div>
        </div>
      </div>
    </>
  );
};

export default BulkUploadModal;
